// src/components/Popup/SaveReportPopup.js
import React, { useState, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Popup from './Popup';
import { parsePhoneNumber, isPossiblePhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import { getSortedCountries, getCountryName, detectCountryFromLocale, isValidCountryCode } from '../../utils/phoneCountryUtils';
import { trackEvent } from '../../utils/Tracking';
import { sendWelcomeEmail } from '../../utils/Onboarding/autoMail';

/**
 * SaveReportPopup - Reusable popup for capturing contact info to save a report
 * 
 * @param {boolean} isOpen - Whether popup is visible
 * @param {Function} onClose - Close handler
 * @param {string} sessionId - Onboarding session ID for tracking
 * @param {number} slideNumber - Current slide number (e.g., 5.5)
 * @param {string} slideName - Short slide name (e.g., 'image_analysis')
 * @param {string} initialCountryCode - Initial country code for phone input (default 'US')
 * @param {Function} onSaveContact - Required callback to save contact entry via parent's sync system
 * @param {Function} onSuccess - Callback after successful save (optional)
 * @param {string} userName - User's full name for welcome email personalization (optional)
 */
export default function SaveReportPopup({
  isOpen,
  onClose,
  sessionId,
  slideNumber,
  slideName,
  initialCountryCode = 'US',
  onSaveContact,
  onSuccess = null,
  userName = '',
}) {
  // Toggle state: 'phone' or 'email'
  const [contactMethod, setContactMethod] = useState('phone');
  
  // Phone input state — restore saved value if available, otherwise detect from browser locale
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const code = initialCountryCode?.toUpperCase();
    if (isValidCountryCode(code)) return code;
    return detectCountryFromLocale();
  });
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const phoneInputRef = useRef(null);
  
  // Email input state
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const emailInputRef = useRef(null);
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get sorted countries for dropdown (priority markets first, then A→Z by name)
  const { priority: priorityCountries, rest: restCountries } = getSortedCountries();
  
  // Build full E.164 phone number
  const getFullPhoneNumber = useCallback(() => {
    if (!phoneInput || !phoneInput.trim()) {
      return '';
    }
    const dialCode = getCountryCallingCode(selectedCountry);
    const cleanedInput = phoneInput.replace(/^\+\d+/, '').trim();
    return `+${dialCode}${cleanedInput}`;
  }, [phoneInput, selectedCountry]);
  
  // Email validation
  const isEmailValid = useMemo(() => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return re.test(String(emailInput || '').trim());
  }, [emailInput]);
  
  // Phone validation
  const validatePhone = useCallback(() => {
    setPhoneError('');
    
    if (!phoneInput || !phoneInput.trim()) {
      setPhoneError('Please enter your phone number.');
      return false;
    }
    
    const fullNumber = getFullPhoneNumber();
    
    if (!isPossiblePhoneNumber(fullNumber)) {
      setPhoneError('Please enter a valid phone number.');
      return false;
    }
    
    return true;
  }, [phoneInput, getFullPhoneNumber]);
  
  // Email validation
  const validateEmail = useCallback(() => {
    setEmailError('');
    
    if (!emailInput || !emailInput.trim()) {
      setEmailError('Please enter your email address.');
      return false;
    }
    
    if (!isEmailValid) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    
    return true;
  }, [emailInput, isEmailValid]);
  
  // Handle continue button click
  const handleContinue = async () => {
    // Validate based on selected method
    if (contactMethod === 'phone') {
      if (!validatePhone()) return;
    } else {
      if (!validateEmail()) return;
    }
    
    setIsSubmitting(true);
    
    try {
      const timestamp = new Date().toISOString();
      const contactValue = contactMethod === 'phone' 
        ? getFullPhoneNumber() 
        : emailInput.trim();
      
      // Build save contact entry
      const saveContactEntry = {
        slideNumber,
        slideName,
        method: contactMethod,
        value: contactValue,
        timestamp,
        ...(contactMethod === 'phone' && {
          dialCode: `+${getCountryCallingCode(selectedCountry)}`,
          countryCode: selectedCountry,
        }),
      };
      
      // Send welcome email if email method is selected
      if (contactMethod === 'email') {
        const emailResult = await sendWelcomeEmail({
          email: contactValue,
          fullName: userName,
          sessionId,
          source: slideName,
          slideNumber,
        });
        
        // Add welcome email result to the entry for Firestore sync
        saveContactEntry.welcomeEmailSent = emailResult.success;
        if (!emailResult.success) {
          saveContactEntry.welcomeEmailError = emailResult.error;
        }
      }
      
      // Save via parent's sync system (goes to latestSnapshot.saveContacts via dirty fields mechanism)
      if (onSaveContact) {
        onSaveContact(saveContactEntry);
      }
      
      // Track event
      await trackEvent('save_report_contact_captured', {
        onboarding_id: sessionId || null,
        slide_number: slideNumber,
        slide_name: slideName,
        contact_method: contactMethod,
        contact_value: contactValue,
        ...(contactMethod === 'phone' && {
          dial_code: `+${getCountryCallingCode(selectedCountry)}`,
          country_code: selectedCountry,
        }),
      });
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess({
          method: contactMethod,
          value: contactValue,
          entry: saveContactEntry,
        });
      }
      
      // Close popup
      onClose();
      
    } catch (error) {
      console.error('[SaveReportPopup] Error saving contact:', error);
      if (contactMethod === 'phone') {
        setPhoneError('Something went wrong. Please try again.');
      } else {
        setEmailError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Check if continue button should be disabled
  const isContinueDisabled = useMemo(() => {
    if (isSubmitting) return true;
    if (contactMethod === 'phone') {
      return !phoneInput || !phoneInput.trim();
    } else {
      return !emailInput || !emailInput.trim();
    }
  }, [isSubmitting, contactMethod, phoneInput, emailInput]);
  
  if (!isOpen) return null;
  
  return ReactDOM.createPortal(
    <Popup 
      isOpen={isOpen}
      onClose={onClose}
      theme="dark"
    >
      <div className="p-6 pt-12">
        {/* Title */}
        <h3 className="text-xl font-bold text-center text-white mb-2">
          Want to save this report?
        </h3>
        
        {/* Subtitle */}
        <p className="text-sm text-center text-gray-400 mb-6">
          We'll send it to you!
        </p>
        
        {/* Toggle: Phone / Email */}
        <div className="flex justify-center mb-6">
          <div 
            className="inline-flex rounded-full p-1"
            style={{ backgroundColor: '#404040' }}
          >
            <button
              type="button"
              onClick={() => {
                setContactMethod('phone');
                setEmailError('');
              }}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                contactMethod === 'phone'
                  ? 'bg-white text-black'
                  : 'bg-transparent text-gray-400 hover:text-white'
              }`}
            >
              phone
            </button>
            <button
              type="button"
              onClick={() => {
                setContactMethod('email');
                setPhoneError('');
              }}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                contactMethod === 'email'
                  ? 'bg-white text-black'
                  : 'bg-transparent text-gray-400 hover:text-white'
              }`}
            >
              email
            </button>
          </div>
        </div>
        
        {/* Dynamic Input Section */}
        <div className="max-w-md mx-auto">
          {contactMethod === 'phone' ? (
            // Phone Input
            <div>
              <div className="flex gap-2 items-center">
                {/* Country Selector */}
                <div className="relative flex-shrink-0 flex items-center gap-1">
                  <select
                    value={selectedCountry}
                    onChange={(e) => {
                      setSelectedCountry(e.target.value);
                      if (phoneError) setPhoneError('');
                      if (phoneInputRef.current) {
                        phoneInputRef.current.focus();
                      }
                    }}
                    autoComplete="tel-country-code"
                    className="phone-country-select h-12 w-12 cursor-pointer bg-transparent border-0 focus:outline-none appearance-none"
                    style={{
                      backgroundImage: `url(https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedCountry}.svg)`,
                      backgroundSize: '36px 24px',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      color: 'transparent',
                    }}
                  >
                    {priorityCountries.map((country) => (
                      <option key={country} value={country}>
                        {getCountryName(country)} +{getCountryCallingCode(country)}
                      </option>
                    ))}
                    <option disabled>──────────────</option>
                    {restCountries.map((country) => (
                      <option key={country} value={country}>
                        {getCountryName(country)} +{getCountryCallingCode(country)}
                      </option>
                    ))}
                  </select>
                  {/* Dropdown arrow */}
                  <div className="pointer-events-none -ml-2">
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                      <path 
                        d="M1 1L5 5L9 1" 
                        stroke="#e5e7eb" 
                        strokeWidth="1.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                
                {/* Phone Input with Country Code Prefix */}
                <div className="flex-1 relative">
                  <div 
                    className="flex items-center h-10 px-4 rounded-xl border bg-transparent border-gray-600 focus-within:ring-2 focus-within:ring-white transition-all"
                  >
                    {/* Non-editable country code prefix */}
                    <span className="font-medium select-none pointer-events-none mr-1 text-gray-400">
                      +{getCountryCallingCode(selectedCountry)}
                    </span>
                    
                    {/* Actual input field */}
                    <input
                      ref={phoneInputRef}
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel-national"
                      name="phone-national"
                      value={phoneInput}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^\d\s\-()]/g, '');
                        setPhoneInput(cleaned);
                        if (phoneError) setPhoneError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleContinue();
                        }
                      }}
                      placeholder="555 123 4567"
                      className="flex-1 bg-transparent border-none outline-none text-white placeholder-gray-500"
                    />
                  </div>
                </div>
              </div>
              
              {/* Phone Error - Fixed height to prevent layout shift */}
              <div className="h-6 mt-2">
                {phoneError && (
                  <div className="text-sm text-red-500">{phoneError}</div>
                )}
              </div>
            </div>
          ) : (
            // Email Input
            <div>
              <input
                ref={emailInputRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Email address"
                className="h-12 px-4 rounded-xl border w-full outline-none text-white bg-transparent border-gray-600 placeholder-gray-500 focus:ring-2 focus:ring-white transition"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  if (emailError) setEmailError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleContinue();
                  }
                }}
              />
              
              {/* Email Error - Fixed height to prevent layout shift */}
              <div className="h-6 mt-2">
                {emailError && (
                  <div className="text-sm text-red-500">{emailError}</div>
                )}
              </div>
            </div>
          )}
          
          {/* Continue Button */}
          <button
            type="button"
            onClick={handleContinue}
            disabled={isContinueDisabled}
            className={`w-full mt-0 py-3 px-6 rounded-full text-sm font-medium transition-all duration-200 ${
              isContinueDisabled
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-white text-black hover:bg-gray-100'
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </Popup>,
    document.body
  );
}
